import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  NewProjectFormData,
  NewProjectImage,
  NewSubProject
} from '../../types/newProject';
import { newProjectService } from '../../services/newProjectService.ts';
import { newValidationService } from '../../services/newValidationService.ts';
import { 
  createNewProject,
  resetSubProjects,
  clearPostulationProject
} from '../../store/actions';

export const useNewProjectCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Validar datos del proyecto para creación
  const validateProjectForCreation = useCallback((projectData: NewProjectFormData): boolean => {
    return newValidationService.validateProjectData(projectData);
  }, []);

  // Validar subproyectos para creación
  const validateSubProjectsForCreation = useCallback((subProjects: NewSubProject[]): boolean => {
    if (subProjects.length === 0) return false;
    return subProjects.every(sub => newValidationService.validateSubProjectData(sub));
  }, []);

  // Validar imágenes para creación
  const validateImagesForCreation = useCallback((images: NewProjectImage[]): boolean => {
    return images.length > 0 && images.every(img => img.file !== null);
  }, []);

  // Crear proyecto con subproyectos
  const createProjectWithSubProjects = useCallback(async (
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ): Promise<void> => {
    // Validar datos antes de crear
    if (!validateProjectForCreation(projectData)) {
      throw new Error('Los datos del proyecto no son válidos');
    }

    if (!validateSubProjectsForCreation(subProjects)) {
      throw new Error('Los subproyectos no son válidos');
    }

    if (!validateImagesForCreation(images)) {
      throw new Error('Las imágenes no son válidas');
    }

    try {
      // Crear proyecto principal
      const createdProject = await newProjectService.createProject(projectData, images);
      
      // Crear subproyectos
      const subProjectPromises = subProjects.map(subProject => 
        newProjectService.createSubProject(subProject, createdProject.id)
      );
      
      await Promise.all(subProjectPromises);

      // Limpiar estado de Redux
      dispatch(resetSubProjects());
      dispatch(clearPostulationProject());

      // Navegar al proyecto creado
      navigate(`/obrasprivadas/${createdProject.id}`);

    } catch (error) {
      console.error('Error al crear proyecto:', error);
      throw error;
    }
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation, dispatch, navigate]);

  // Crear proyecto usando Redux (compatibilidad con sistema actual)
  const createProjectWithRedux = useCallback(async (
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    projectImages: NewProjectImage[]
  ): Promise<void> => {
    // Validar datos
    if (!validateProjectForCreation(projectData)) {
      throw new Error('Los datos del proyecto no son válidos');
    }

    if (!validateSubProjectsForCreation(subProjects)) {
      throw new Error('Los subproyectos no son válidos');
    }

    try {
      // Transformar datos para compatibilidad con Redux
      const proyectoForRedux = {
        ...projectData,
        image: projectImages.map(img => img.file).filter(file => file !== null)
      };

      // Usar la acción de Redux existente
      await (dispatch as any)(createNewProject(proyectoForRedux, subProjects, navigate));
    } catch (error) {
      console.error('Error al crear proyecto con Redux:', error);
      throw error;
    }
  }, [validateProjectForCreation, validateSubProjectsForCreation, dispatch, navigate]);

  // Generar ID temporal para subproyectos
  const generateTempSubProjectId = useCallback((): string => {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Preparar subproyecto para creación
  const prepareSubProjectForCreation = useCallback((subProject: NewSubProject): NewSubProject => {
    return {
      ...subProject,
      id: subProject.id || generateTempSubProjectId(),
      isNew: true,
      isEdited: false
    };
  }, [generateTempSubProjectId]);

  // Preparar proyecto para creación
  const prepareProjectForCreation = useCallback((projectData: NewProjectFormData): NewProjectFormData => {
    return {
      ...projectData,
      // Asegurar que las fechas estén en el formato correcto
      startDate: projectData.startDate || '',
      endDate: projectData.endDate || ''
    };
  }, []);

  // Verificar si se puede publicar
  const canPublishProject = useCallback((
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ): boolean => {
    return validateProjectForCreation(projectData) && 
           validateSubProjectsForCreation(subProjects) && 
           validateImagesForCreation(images);
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation]);

  // Obtener errores de validación para creación
  const getCreationValidationErrors = useCallback((
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ) => {
    const errors: string[] = [];

    if (!validateProjectForCreation(projectData)) {
      errors.push('Los datos del proyecto no son válidos');
    }

    if (!validateSubProjectsForCreation(subProjects)) {
      errors.push('Debe agregar al menos un subproyecto válido');
    }

    if (!validateImagesForCreation(images)) {
      errors.push('Debe agregar al menos una imagen');
    }

    return errors;
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation]);

  // Configuración específica para creación
  const creationConfig = useMemo(() => ({
    maxSubProjects: 10,
    minSubProjects: 1,
    maxImages: 5,
    minImages: 1,
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxImageSize: 5 * 1024 * 1024, // 5MB
    dateFormat: 'YYYY-MM-DD',
    timezone: 'America/Argentina/Buenos_Aires'
  }), []);

  return {
    // Validaciones
    validateProjectForCreation,
    validateSubProjectsForCreation,
    validateImagesForCreation,
    canPublishProject,
    getCreationValidationErrors,
    
    // Operaciones
    createProjectWithSubProjects,
    createProjectWithRedux,
    
    // Utilidades
    generateTempSubProjectId,
    prepareSubProjectForCreation,
    prepareProjectForCreation,
    
    // Configuración
    creationConfig
  };
};
